import React, { useState, useContext } from "react";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import PropTypes from "prop-types";
import { isEmpty, toSafeInteger } from "lodash";
import { useRouter } from "next/router";

import Spinner from "components/Spinner";
import { constantCase } from "util/strings";
import useToast from "hooks/useToast";
import useIsCityInDb from "hooks/useIsCityInDb";
import { LEASE_TYPE, EMAIL_REGEX } from "util/constants";
import SignInModal from "components/componentLibrary/Modals/SignInModal";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import {
  CreateRenterSearchActiveRequirementsMutation,
  determineLeaseTerm,
} from "components/ClientSearch/Filters/SaveActiveRequirementsButton";
import { useCurrentUser } from "hooks";
import { trackSignupFormSubmit } from "util/ga4";

import styles from "./style.module.scss";

const RENTER_ONBOARDING_MUTATION = gql`
  mutation RenterOnboardingNoEmployeeCTAMutation(
    $email: String!
    $phone: String
    $cityOfInterest: CityName
    $marketOfInterest: ID
    $numberOfEmployees: Int
    $squareFeet: Int
    $neighborhoodIds: [ID]
    $neighborhoodNames: [String]
    $signUpType: String
    $source: String
    $areaExternalIds: [ID]
  ) {
    renterOnboarding(
      input: {
        renter: { phone: $phone, email: $email, signUpType: $signUpType }
        cityOfInterest: $cityOfInterest
        marketOfInterest: $marketOfInterest
        areaExternalIds: $areaExternalIds
        onboardingInfo: {
          source: $source
          neighborhoodIds: $neighborhoodIds
          numberOfEmployees: $numberOfEmployees
          squareFeet: $squareFeet
          neighborhoodNames: $neighborhoodNames
        }
      }
    ) {
      onboardingId
      created
      neighborhoodNames
      currentUser {
        id
        salesforceId
        email
        uuid
        firstName
        lastName
        phone
        e164Phone
        companyName
        displayName
        isRenter
        isBroker
        isVerified
        isAdmin
        hasPassword
        featureFlags
        profilePicURL
        brokerMarket {
          id
          name
          state
        }
        completedUserTasks {
          id
          task
        }
        confirmed
        unconfirmedEmail
        cityOfInterest
        marketOfInterest {
          id
          name
          position {
            lat
            lng
          }
        }
        tours {
          id
          date
        }
        latestCampaign {
          id
          uuid
          listings {
            id
          }
          surveys {
            nodes {
              id
              externalId
            }
          }
          currentActiveRequirements(setByUser: true) {
            id
            neighborhoods {
              id
              name
            }
            minPsf
            maxPsf
            minSquareFeet
            maxSquareFeet
            minMonthlyBudget
            maxMonthlyBudget
            numberOfEmployees
            minSqft: minSquareFeet
            maxSqft: maxSquareFeet
            maxPricePerSquareFoot: maxPsf
            minPricePerSquareFoot: minPsf
            maxMonthlyPrice: maxMonthlyBudget
            minMonthlyPrice: minMonthlyBudget
            leaseTerm
            areas {
              id
              externalId
              name
            }
            market {
              id
              name
              position {
                lat
                lng
              }
            }
          }
        }
      }
      errors {
        auth
      }
    }
  }
`;

const NoOfEmployeesEngagementBox = ({ areas, market, city, lead, source, listingId }) => {
  const [numberOfEmployees, setNumberOfEmployees] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [onboardRenter, { loading }] = useMutation(RENTER_ONBOARDING_MUTATION);
  const [createRenterSearchActiveRequirements] = useMutation(
    CreateRenterSearchActiveRequirementsMutation
  );
  const { showToast } = useToast();
  const isCityInDB = useIsCityInDb(city);
  const { values } = useContext(QueryManagerContext);
  const router = useRouter();
  const { login } = useCurrentUser();

  const onSubmit = async (e) => {
    e.preventDefault();
    const variables = {
      email,
      phone,
      marketOfInterest: market,
      cityOfInterest: city && !market && isCityInDB ? constantCase(city) : null,
      numberOfEmployees,
      areaExternalIds: areas
        .filter(Boolean)
        .map(({ externalId }) => externalId),
      squareFeet: parseInt(numberOfEmployees * 150, 10),
      signUpType: lead,
      source,
    };

    const { data } = await onboardRenter({ variables });
    if (data?.renterOnboarding?.currentUser) {
      const pageType = router.asPath.includes("/building/")
        ? "building_detail"
        : "listing_index";
      trackSignupFormSubmit({
        pageType,
        location: city || market,
        listingId: listingId || undefined,
      });
      await login();
      showToast("Welcome to Squarefoot!");
      if (!isEmpty(values) && router.asPath.includes("office-space/m/"))
        createRenterSearchActiveRequirements({
          variables: {
            marketExternalId: values.market.externalId,
            leaseTerm: determineLeaseTerm({
              leaseTermMin: values.leaseTermMin,
              leaseTermMax: values.leaseTermMax,
            }),
            statuses: [
              "available",
              "offers_in",
              "squarefoot_offers_in",
              "lease_out",
              "squarefoot_lease_out",
            ],
            leaseTypes: [...Object.keys(LEASE_TYPE), "UNKNOWN"],
            campaignId: data?.renterOnboarding?.currentUser.latestCampaign.id,
            minPricePerSquareFoot: values.minPricePerSquareFoot,
            maxPricePerSquareFoot: values.maxPricePerSquareFoot,
            minSquareFeet: values.minSqft,
            maxSquareFeet: values.maxSqft,
            minMonthlyBudget: values.minMonthlyPrice,
            maxMonthlyBudget: values.maxMonthlyPrice,
            numberOfEmployees: toSafeInteger(numberOfEmployees),
            areaExternalIds: [
              ...values.submarkets.map(({ externalId }) => externalId),
              ...values.neighborhoods.map(({ externalId }) => externalId),
            ],
          },
        });
    }
    if (
      data?.renterOnboarding?.errors?.auth?.includes(
        "Login and password do not match."
      )
    ) {
      setShowSignInModal(true);
    }
  };

  return (
    <>
      <div className={styles.engagementCard}>
        <p className={styles.title}>
          Sign up to see listings tailored to your business needs.
        </p>
        <p className={styles.subtitle}>
          Create your profile to access new listings, save your search, and get
          recommendations from a specialist.
        </p>
        <form onSubmit={onSubmit} data-qa="noOfEmployeesEngagementForm">
          <div className={styles.inputsContainer}>
            <input
              className={styles.input}
              type="email"
              pattern={EMAIL_REGEX}
              title={`${email} is not a valid email`}
              required
              name="email"
              placeholder="Business Email"
              data-qa="email"
              onChange={(evt) => {
                setEmail(evt.target.value);
              }}
              value={email}
            />
            <input
              className={styles.input}
              type="tel"
              required
              name="phone"
              placeholder="Phone Number"
              data-qa="phone"
              onChange={(evt) => {
                setPhone(evt.target.value);
              }}
              value={phone}
            />
            <input
              className={styles.input}
              type="number"
              required
              min="1"
              max="100000"
              name="noOfEmployees"
              placeholder="# of employees"
              data-qa="noOfEmployeeEngagement"
              onChange={(evt) => {
                setNumberOfEmployees(parseInt(evt.target.value, 10) || "");
              }}
              value={numberOfEmployees || ""}
            />
            <button
              type="submit"
              className={`btn-primary ${styles.button}`}
              disabled={loading}
            >
              {loading ? (
                <Spinner size={12} className={styles.spinner} />
              ) : (
                "Sign Up"
              )}
            </button>
          </div>
        </form>
      </div>
      {showSignInModal && (
        <SignInModal
          existingEmail={email}
          onClose={() => setShowSignInModal(false)}
        />
      )}
    </>
  );
};

NoOfEmployeesEngagementBox.propTypes = {
  market: PropTypes.string,
  city: PropTypes.string,
  areas: PropTypes.arrayOf(
    PropTypes.shape({ externalId: PropTypes.string.isRequired })
  ).isRequired,
  lead: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  listingId: PropTypes.string,
};

NoOfEmployeesEngagementBox.defaultProps = {
  market: "",
  city: "",
  listingId: null,
};

export default NoOfEmployeesEngagementBox;
